// src/context/AuthContext.jsx

import React, { createContext, useState, useEffect, useCallback } from 'react';
import api, {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  getStoredUser,
  setStoredUser,
  clearStoredUser,
} from '../utils/api';
import { identifyUser, resetIdentity, trackEvent } from '../utils/analytics';

// Create the Auth Context
export const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAuthToken());
  const [user, setUser] = useState(getStoredUser());

  // Logout function
  const logout = useCallback(() => {
    clearAuthToken();
    clearStoredUser();
    delete api.defaults.headers.common['x-auth-token'];
    setIsAuthenticated(false);
    setUser(null);
    resetIdentity();
    window.dispatchEvent(new CustomEvent('eventory:auth-state-changed'));
  }, []);

  // Load user data
  const loadUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/user');
      setUser(res.data);
      setIsAuthenticated(true);
      setStoredUser(res.data);
    } catch (err) {
      console.error('Error loading user:', err);
      logout();
    }
  }, [logout]);

  // Load user on app load
  useEffect(() => {
    if (isAuthenticated && !user) {
      loadUser();
    }
  }, [isAuthenticated, loadUser, user]);

  // Login function
  const login = async (emailOrUsername, password) => {
    try {
      const res = await api.post('/auth/login', { emailOrUsername, password });
      const { token, user } = res.data;
      setAuthToken(token);
      api.defaults.headers.common['x-auth-token'] = token;
      setUser(user);
      setIsAuthenticated(true);
      setStoredUser(user);
      if (user?._id) {
        identifyUser(user._id, { name: user.name, email: user.email });
        trackEvent('login_success');
      }
      window.dispatchEvent(new CustomEvent('eventory:auth-state-changed'));
    } catch (err) {
      console.error('Login error:', err.response?.data?.msg);
      throw err.response?.data?.msg || 'Login failed';
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
